import { useState, useEffect } from "react";
import {
  Shield,
  Plus,
  Edit,
  Trash2,
  Save,
  X,
  Users,
  Search,
  RefreshCw,
  Loader2,
  AlertCircle,
  Check,
  Power,
  PowerOff,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Switch } from "./ui/switch";
import Swal from "sweetalert2";

const RolesGestion = () => {
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [editingRole, setEditingRole] = useState(null);
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    active: true,
    tipo_empresa_aplicable: "all",
  });
  const [errors, setErrors] = useState({});

  useEffect(() => {
    loadRoles();
    checkIfSuperAdmin();
  }, []);
  const checkIfSuperAdmin = () => {
    const user = JSON.parse(localStorage.getItem("user") || "{}");
    setIsSuperAdmin(user.empresa_id === null);
    console.log("🔍 Usuario es Super Admin:", user.empresa_id === null);
  };
  const getApiUrl = () => {
    const apiUrl = process.env.REACT_APP_API_URL;

    if (!apiUrl) {
      console.warn(
        "⚠️ REACT_APP_API_URL no está definida en el archivo .env. Usando fallback http://localhost:8000/api",
      );
      return "http://localhost:8000/api";
    }

    // Asegurar que siempre termina en /api
    return apiUrl.endsWith("/api") ? apiUrl : `${apiUrl}/api`;
  };

  const getHeaders = () => ({
    "Content-Type": "application/json",
    Accept: "application/json",
    Authorization: `Bearer ${localStorage.getItem("token")}`,
  });

  const loadRoles = async () => {
    setLoading(true);
    try {
      console.log("🔄 Cargando roles desde:", `${getApiUrl()}/roles`);

      const response = await fetch(`${getApiUrl()}/roles`, {
        headers: getHeaders(),
      });

      console.log("📥 Load roles status:", response.status);

      if (response.ok) {
        const data = await response.json();
        console.log("✅ Roles data recibida:", data);

        if (data.success && data.data) {
          setRoles(data.data);
          console.log("✅ Roles cargados exitosamente:", data.data.length);
        } else {
          setRoles([]);
          console.warn("⚠️ No hay roles en la respuesta");
        }
      } else {
        const errorData = await response.json();
        console.error("❌ Error response:", errorData);
        throw new Error(errorData.message || "Error al cargar roles");
      }
    } catch (error) {
      console.error("❌ Error cargando roles:", error);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "No se pudieron cargar los roles",
      });
      setRoles([]);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenModal = (role = null) => {
    if (role) {
      setEditingRole(role);
      setFormData({
        name: role.name,
        description: role.description,
        active: role.active,
        tipo_empresa_aplicable: role.tipo_empresa_aplicable || "all",
      });
      console.log("📝 Editando rol:", role);
    } else {
      setEditingRole(null);
      setFormData({
        name: "",
        description: "",
        active: true,
        tipo_empresa_aplicable: "all",
      });
      console.log("➕ Creando nuevo rol");
    }
    setErrors({});
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingRole(null);
    setFormData({
      name: "",
      description: "",
      active: true,
      tipo_empresa_aplicable: "all",
    });
    setErrors({});
    console.log("❌ Modal cerrado");
  };

  const handleNameChange = (e) => {
    let value = e.target.value;
    // Convertir a minúsculas y reemplazar espacios con guiones bajos
    value = value.toLowerCase().replace(/\s+/g, "_");
    // Remover caracteres no permitidos (solo permitir letras, números y guiones bajos)
    value = value.replace(/[^a-z0-9_]/g, "");

    setFormData({ ...formData, name: value });

    // Limpiar error si había
    if (errors.name) {
      const newErrors = { ...errors };
      delete newErrors.name;
      setErrors(newErrors);
    }
  };

  const handleDescriptionChange = (e) => {
    setFormData({ ...formData, description: e.target.value });

    // Limpiar error si había
    if (errors.description) {
      const newErrors = { ...errors };
      delete newErrors.description;
      setErrors(newErrors);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrors({});

    // ✅ Validación básica en frontend
    if (!formData.name || !formData.description) {
      Swal.fire({
        icon: "warning",
        title: "Campos incompletos",
        text: "Por favor completa el nombre y la descripción del rol",
      });
      return;
    }

    if (formData.name.length < 3) {
      Swal.fire({
        icon: "warning",
        title: "Nombre muy corto",
        text: "El nombre debe tener al menos 3 caracteres",
      });
      return;
    }

    const url = editingRole
      ? `${getApiUrl()}/roles/${editingRole.id}`
      : `${getApiUrl()}/roles`;

    const method = editingRole ? "PUT" : "POST";

    try {
      const payload = {
        name: formData.name.trim(),
        description: formData.description.trim(),
        active: formData.active ?? true,
      };
      // ✅ Si es Super Admin creando un rol nuevo, agregar tipo_empresa_aplicable
      if (isSuperAdmin && !editingRole) {
        payload.tipo_empresa_aplicable = formData.tipo_empresa_aplicable;
      }
      console.log("📤 Enviando payload al backend:", payload);
      console.log("🌐 URL:", url);
      console.log("🔧 Method:", method);

      const response = await fetch(url, {
        method,
        headers: getHeaders(),
        body: JSON.stringify(payload),
      });

      console.log("📥 Response status:", response.status);
      console.log("📥 Response ok:", response.ok);

      // ✅ Intentar parsear la respuesta
      let data;
      try {
        data = await response.json();
        console.log("📥 Response data completa:", data);
      } catch (parseError) {
        console.error("❌ Error parseando respuesta:", parseError);
        throw new Error("Respuesta del servidor inválida");
      }

      // ✅ Verificar si fue exitoso
      if (response.ok && data.success) {
        console.log("✅ Operación exitosa");

        Swal.fire({
          icon: "success",
          title: editingRole ? "Rol actualizado" : "Rol creado",
          text: data.message || "Operación exitosa",
          toast: true,
          position: "top-end",
          showConfirmButton: false,
          timer: 2000,
        });

        handleCloseModal();
        await loadRoles(); // ✅ Esperar a que se recarguen los roles
      } else {
        // ❌ Manejar errores
        console.error("❌ Error en respuesta:", data);

        // Si hay errores de validación (422)
        if (data.errors) {
          console.log("📋 Errores de validación:", data.errors);
          setErrors(data.errors);

          // Construir mensaje de errores
          const errorMessages = Object.entries(data.errors)
            .map(([field, messages]) => {
              const fieldName =
                field === "name"
                  ? "Nombre"
                  : field === "description"
                    ? "Descripción"
                    : field;
              return `${fieldName}: ${
                Array.isArray(messages) ? messages[0] : messages
              }`;
            })
            .join("\n");

          Swal.fire({
            icon: "error",
            title: "Error de validación",
            html: errorMessages.replace(/\n/g, "<br>"),
            confirmButtonText: "Entendido",
          });
        } else {
          // Otros tipos de error
          throw new Error(data.message || "Error al guardar el rol");
        }
      }
    } catch (error) {
      console.error("❌ Error completo:", error);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: error.message || "No se pudo guardar el rol",
      });
    }
  };

  const handleDelete = async (roleId, roleName) => {
    const result = await Swal.fire({
      title: "¿Eliminar rol?",
      html: `Se eliminará el rol <strong>${roleName}</strong> y todos sus permisos asociados.`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Sí, eliminar",
      cancelButtonText: "Cancelar",
    });

    if (result.isConfirmed) {
      try {
        console.log("🗑️ Eliminando rol:", roleId);

        const response = await fetch(`${getApiUrl()}/roles/${roleId}`, {
          method: "DELETE",
          headers: getHeaders(),
        });

        console.log("📥 Delete response status:", response.status);

        const data = await response.json();
        console.log("📥 Delete response data:", data);

        if (response.ok && data.success) {
          Swal.fire({
            icon: "success",
            title: "Eliminado",
            text: data.message || "Rol eliminado exitosamente",
            timer: 2000,
            showConfirmButton: false,
          });
          await loadRoles();
        } else {
          throw new Error(data.message || "Error al eliminar");
        }
      } catch (error) {
        console.error("❌ Error eliminando rol:", error);
        Swal.fire({
          icon: "error",
          title: "Error",
          text: error.message,
        });
      }
    }
  };

  const handleToggleActive = async (roleId, currentActive) => {
    try {
      console.log(
        "🔄 Cambiando estado del rol:",
        roleId,
        "Estado actual:",
        currentActive,
      );

      const response = await fetch(
        `${getApiUrl()}/roles/${roleId}/toggle-active`,
        {
          method: "PATCH",
          headers: getHeaders(),
        },
      );

      console.log("📥 Toggle response status:", response.status);

      const data = await response.json();
      console.log("📥 Toggle response data:", data);

      if (response.ok && data.success) {
        Swal.fire({
          icon: "success",
          title: data.message,
          toast: true,
          position: "top-end",
          showConfirmButton: false,
          timer: 1500,
        });
        await loadRoles();
      } else {
        throw new Error(data.message || "Error al cambiar estado");
      }
    } catch (error) {
      console.error("❌ Error cambiando estado:", error);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: error.message,
      });
    }
  };

  const filteredRoles = roles.filter(
    (role) =>
      role.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      role.description.toLowerCase().includes(searchTerm.toLowerCase()),
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
        <span className="ml-2 text-gray-600">Cargando roles...</span>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <Shield className="h-5 w-5 text-purple-600" />
              <CardTitle>Gestión de Roles</CardTitle>
              <Badge variant="secondary">{roles.length} roles</Badge>
            </div>
            <div className="flex space-x-2">
              <Button variant="outline" size="sm" onClick={loadRoles}>
                <RefreshCw className="h-4 w-4 mr-1" />
                Actualizar
              </Button>
              <Button size="sm" onClick={() => handleOpenModal()}>
                <Plus className="h-4 w-4 mr-1" />
                Nuevo Rol
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {/* Búsqueda */}
          <div className="relative mb-4">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <Input
              type="text"
              placeholder="Buscar roles..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>

          {/* Lista de roles */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredRoles.map((role) => (
              <Card key={role.id} className="hover:shadow-md transition-shadow">
                <CardContent className="p-4">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex-1">
                      <div className="flex items-center space-x-2 mb-1">
                        <h3 className="font-semibold text-lg capitalize">
                          {role.name}
                        </h3>
                        <Badge
                          variant={role.active ? "default" : "secondary"}
                          className="text-xs"
                        >
                          {role.active ? (
                            <>
                              <Check className="h-3 w-3 mr-1" /> Activo
                            </>
                          ) : (
                            <>
                              <X className="h-3 w-3 mr-1" /> Inactivo
                            </>
                          )}
                        </Badge>
                      </div>
                      <p className="text-sm text-gray-600 line-clamp-2">
                        {role.description}
                      </p>
                    </div>
                  </div>

                  {/* Estadísticas */}
                  <div className="flex items-center space-x-4 text-xs text-gray-500 mb-3">
                    <div className="flex items-center">
                      <Users className="h-3 w-3 mr-1" />
                      <span>{role.users_count || 0} usuarios</span>
                    </div>
                    <div className="flex items-center">
                      <Shield className="h-3 w-3 mr-1" />
                      <span>{role.permissions_count || 0} permisos</span>
                    </div>
                  </div>

                  {/* Acciones */}
                  <div className="flex space-x-2">
                    <Button
                      variant="outline"
                      size="sm"
                      className="flex-1"
                      onClick={() => handleOpenModal(role)}
                      disabled={!role.editable}
                    >
                      <Edit className="h-3 w-3 mr-1" />
                      Editar
                    </Button>
                    <Button
                      variant={role.active ? "secondary" : "default"}
                      size="sm"
                      onClick={() => handleToggleActive(role.id, role.active)}
                      title={role.active ? "Desactivar" : "Activar"}
                    >
                      {role.active ? (
                        <PowerOff className="h-3 w-3" />
                      ) : (
                        <Power className="h-3 w-3" />
                      )}
                    </Button>
                    <Button
                      variant="destructive"
                      size="sm"
                      onClick={() => handleDelete(role.id, role.name)}
                      disabled={!role.eliminable}
                    >
                      <Trash2 className="h-3 w-3" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {filteredRoles.length === 0 && (
            <div className="text-center py-12">
              <Shield className="h-12 w-12 mx-auto mb-3 text-gray-300" />
              <p className="text-gray-500">
                {searchTerm
                  ? "No se encontraron roles"
                  : "No hay roles registrados"}
              </p>
              {!searchTerm && (
                <Button
                  className="mt-4"
                  size="sm"
                  onClick={() => handleOpenModal()}
                >
                  <Plus className="h-4 w-4 mr-1" />
                  Crear primer rol
                </Button>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Modal de Crear/Editar */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-md">
            <CardHeader className="border-b">
              <CardTitle className="flex items-center">
                <Shield className="h-5 w-5 mr-2 text-purple-600" />
                {editingRole ? "Editar Rol" : "Nuevo Rol"}
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-6">
              <form onSubmit={handleSubmit} className="space-y-4">
                {/* Nombre */}
                <div>
                  <label className="block text-sm font-medium mb-1">
                    Nombre del Rol <span className="text-red-500">*</span>
                  </label>
                  <Input
                    type="text"
                    value={formData.name}
                    onChange={handleNameChange}
                    placeholder="Ej: supervisor_operaciones"
                    className={errors.name ? "border-red-500" : ""}
                    maxLength={100}
                  />
                  {errors.name && (
                    <p className="text-xs text-red-500 mt-1 flex items-center">
                      <AlertCircle className="h-3 w-3 mr-1" />
                      {Array.isArray(errors.name)
                        ? errors.name[0]
                        : errors.name}
                    </p>
                  )}
                  <p className="text-xs text-gray-500 mt-1">
                    Solo minúsculas, números y guiones bajos. Los espacios se
                    convertirán automáticamente.
                  </p>
                </div>

                {/* Descripción */}
                <div>
                  <label className="block text-sm font-medium mb-1">
                    Descripción <span className="text-red-500">*</span>
                  </label>
                  <Textarea
                    value={formData.description}
                    onChange={handleDescriptionChange}
                    placeholder="Descripción del rol y sus responsabilidades..."
                    rows={3}
                    className={errors.description ? "border-red-500" : ""}
                    maxLength={255}
                  />
                  {errors.description && (
                    <p className="text-xs text-red-500 mt-1 flex items-center">
                      <AlertCircle className="h-3 w-3 mr-1" />
                      {Array.isArray(errors.description)
                        ? errors.description[0]
                        : errors.description}
                    </p>
                  )}
                  <p className="text-xs text-gray-500 mt-1">
                    {formData.description.length}/255 caracteres
                  </p>
                </div>
                {/* Tipo de Empresa (solo para Super Admin) */}
                {isSuperAdmin && !editingRole && (
                  <div>
                    <label className="block text-sm font-medium mb-1">
                      Tipo de Empresa Aplicable{" "}
                      <span className="text-red-500">*</span>
                    </label>
                    <select
                      value={formData.tipo_empresa_aplicable}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          tipo_empresa_aplicable: e.target.value,
                        })
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                    >
                      <option value="all">Todas las empresas</option>
                      <option value="TRANSPORTISTA">Transportistas</option>
                      <option value="OPERADOR_LOGISTICO">Operadores</option>
                      <option value="CLIENTE">Clientes</option>
                    </select>
                    {errors.tipo_empresa_aplicable && (
                      <p className="text-xs text-red-500 mt-1 flex items-center">
                        <AlertCircle className="h-3 w-3 mr-1" />
                        {Array.isArray(errors.tipo_empresa_aplicable)
                          ? errors.tipo_empresa_aplicable[0]
                          : errors.tipo_empresa_aplicable}
                      </p>
                    )}
                    <p className="text-xs text-gray-500 mt-1">
                      Define para qué tipo de empresas estará disponible este
                      rol
                    </p>
                  </div>
                )}
                {/* Estado */}
                <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div className="flex items-center space-x-2">
                    <label className="text-sm font-medium">Estado Activo</label>
                    <Badge
                      variant={formData.active ? "default" : "secondary"}
                      className="text-xs"
                    >
                      {formData.active ? "Activo" : "Inactivo"}
                    </Badge>
                  </div>
                  <Switch
                    checked={formData.active}
                    onCheckedChange={(checked) =>
                      setFormData({ ...formData, active: checked })
                    }
                  />
                </div>

                {/* Botones */}
                <div className="flex space-x-2 pt-4 border-t">
                  <Button
                    type="button"
                    variant="outline"
                    className="flex-1"
                    onClick={handleCloseModal}
                  >
                    <X className="h-4 w-4 mr-1" />
                    Cancelar
                  </Button>
                  <Button type="submit" className="flex-1">
                    <Save className="h-4 w-4 mr-1" />
                    {editingRole ? "Actualizar" : "Crear Rol"}
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default RolesGestion;
